import { useEffect, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import Button from "@/components/Button";
import MarkdownReader from "@/components/affiliate/MarkdownReader";
import { affGet, affPost } from "@/lib/affiliateApi";
import { toast } from "@/components/Toast";
import { useAffiliateAuth } from "@/lib/affiliateAuth";
import { C } from "@/lib/brand";

// Foundational compliance framework reader. Two responsibilities:
//   1. Render the BE-shipped markdown document with proper typography.
//   2. Capture an electronic acknowledgement (per BE contract:
//      POST /affiliate/content/compliance/ack with {signature}).
//
// API contract (coordinate with BE):
//   GET /affiliate/content/compliance →
//     { title, body_md, version, lastUpdatedAt,
//       acknowledgement: { required, acknowledgedAt, signature, version } }
//   POST /affiliate/content/compliance/ack { signature } →
//     { acknowledgedAt, signature, version }
export default function AssetsCompliance() {
  const { profile } = useAffiliateAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchDoc = async () => {
    try {
      const res = await affGet("/affiliate/content/foundational", { auth: true });
      // Foundational endpoint returns the active foundational content
      // (Compliance Framework). Older builds called this /compliance —
      // we mirror that shape so the rest of this component doesn't change.
      const piece = Array.isArray(res?.items) ? res.items[0] : (res?.item || res);
      setDoc({
        title: piece?.title || "Affiliate Compliance Framework",
        body_md: piece?.body || piece?.body_md || "",
        version: piece?.version,
        lastUpdatedAt: piece?.lastEditedByAdminAt || piece?.publishedAt || piece?.lastUpdatedAt,
        acknowledgement: piece?.acknowledgement || res?.acknowledgement || {},
      });
    } catch (err) {
      setDoc({ __error: err?.detail || "Content not available yet." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDoc(); }, []);

  const submitAck = async () => {
    const sig = signature.trim();
    if (sig.length < 2) {
      toast("Please type your full legal name to acknowledge.", "error");
      return;
    }
    setSubmitting(true);
    try {
      await affPost("/affiliate/content/compliance/ack", { signature: sig }, { auth: true });
      toast("Compliance Framework acknowledged.", "success");
      await fetchDoc();
    } catch (err) {
      toast(err?.detail || "Could not record acknowledgement. Try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  if (doc?.__error) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "56px 24px" }}>
        <Eyebrow color={C.peachDeep}>★ START HERE</Eyebrow>
        <Headline before="COMPLIANCE" italic="framework." size={48} className="mt-4" />
        <div data-testid="compliance-empty" style={{ marginTop: 24, padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
          We're loading the framework into your portal — check back in a few hours and we'll email you when it's ready.
        </div>
      </div>
    );
  }

  const ack = doc?.acknowledgement || {};
  const isAcknowledged = !!ack.acknowledgedAt;
  const versionMatches = !ack.version || !doc.version || ack.version === doc.version;
  const needsAck = ack.required && (!isAcknowledged || !versionMatches);

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ START HERE</Eyebrow>
      <Headline
        before={doc.title ? doc.title.split(" ").slice(0, -1).join(" ") || "COMPLIANCE" : "COMPLIANCE"}
        italic={doc.title ? doc.title.split(" ").slice(-1)[0] + "." : "framework."}
        size={48}
        className="mt-4"
      />
      <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: C.warmGray, letterSpacing: "0.08em" }}>
        {doc.version ? `v${doc.version} · ` : ""}
        {doc.lastUpdatedAt ? `UPDATED ${new Date(doc.lastUpdatedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}` : ""}
        {isAcknowledged && (
          <span data-testid="compliance-ack-stamp" style={{ marginLeft: 14, color: C.sageDeep }}>
            ✓ ACKNOWLEDGED {new Date(ack.acknowledgedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
            {!versionMatches && " — new version available, please re-sign."}
          </span>
        )}
      </div>

      <div style={{ marginTop: 32 }}>
        <MarkdownReader source={doc.body_md} testId="compliance-body" />
      </div>

      {needsAck && (
        <div
          data-testid="compliance-ack-block"
          style={{
            marginTop: 48,
            padding: 28,
            background: C.peach,
            color: C.ink,
            border: `2px solid ${C.peachDeep}`,
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDark }}>
            ★ ACKNOWLEDGE TO CONTINUE
          </div>
          <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6, color: C.ink }}>
            I confirm I have read the VOYA9 Affiliate Compliance Framework and agree to follow its disclosure, claims, and brand-voice rules in all VOYA9-related content I create.
          </p>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: C.peachDark,
              marginTop: 18,
              marginBottom: 8,
            }}
          >
            SIGNATURE (TYPE FULL LEGAL NAME) <span style={{ color: C.coral }}>*</span>
          </label>
          <input
            type="text"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder={
              profile?.firstName && profile?.lastName
                ? `${profile.firstName} ${profile.lastName}`
                : "Your full legal name"
            }
            data-testid="compliance-ack-signature"
            autoComplete="off"
            maxLength={200}
            style={{
              width: "100%",
              padding: "14px 16px",
              background: C.pure,
              border: `1.5px solid ${C.peachDark}`,
              fontSize: 18,
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
              color: C.ink,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <div style={{ marginTop: 16 }}>
            <Button variant="primary" testId="compliance-ack-submit" onClick={submitAck} disabled={submitting}>
              {submitting ? <Spinner /> : "ACKNOWLEDGE & SAVE →"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
